import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';
import type { Palette, Theme } from '@mui/material/styles';
import { ThemeProvider, createTheme, responsiveFontSizes } from '@mui/material/styles';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { StorageE } from 'declarations/enums/storage';
import { ChirpDarkTheme, ChirpTheme } from 'theme/warrcloud';

interface ThemeProps {
  children: React.ReactNode;
}

const createScrollbarStyles = (palette: Palette): Record<string, unknown> => ({
  '*': {
    scrollbarWidth: 'thin',
    scrollbarColor: `${palette.text?.secondary} transparent`,
  },
  '*::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
    backgroundColor: 'transparent',
  },
  '*::-webkit-scrollbar-track': {
    backgroundColor: 'transparent',
  },
  '*::-webkit-scrollbar-thumb': {
    backgroundColor: palette.text?.secondary,
    borderRadius: '4px',
  },
  '*::-webkit-scrollbar-thumb:hover': {
    backgroundColor: palette.text?.secondary,
    cursor: 'pointer',
  },
  '*::-webkit-scrollbar-corner': {
    backgroundColor: 'transparent',
  },
});

declare module '@mui/material/styles' {
  interface Palette {
    carrot: Palette['primary'];
    grass: Palette['primary'];
    header: { blue: string };
    border: { outlined: string };
    workItemRow: { assigned: string; assignedHover: string; hoverNeutral: string };
    workItemType: { error: string; info: string; default: string };
  }

  interface PaletteOptions {
    carrot?: PaletteOptions['primary'];
    grass?: PaletteOptions['primary'];
    header?: { blue?: string };
    border?: { outlined?: string };
    workItemRow?: { assigned?: string; assignedHover?: string; hoverNeutral?: string };
    workItemType?: { error?: string; info?: string; default?: string };
  }

  interface PaletteColor {
    highlight?: string;
    hover?: string;
    selected?: string;
    border50?: string;
    tint08?: string;
    chipBg?: string;
    hoverBorder?: string;
    hoverContainedBg?: string;
    hoverContainedBorder?: string;
  }
  interface SimplePaletteColorOptions {
    highlight?: string;
    hover?: string;
    selected?: string;
    border50?: string;
    tint08?: string;
    chipBg?: string;
    hoverBorder?: string;
    hoverContainedBg?: string;
    hoverContainedBorder?: string;
  }

  interface TypeBackground {
    backdrop?: string;
    paperElevation0?: string;
    paperElevation1?: string;
    paperElevation2?: string;
    paperElevation16?: string;
    paperElevation24?: string;
  }
}

export enum ThemeModeE {
  Light = 'light',
  Dark = 'dark',
}

interface ThemeModeContextValue {
  mode: ThemeModeE;
  toggleMode: () => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue | undefined>(undefined);

export const useThemeMode = (): ThemeModeContextValue => {
  const ctx = useContext(ThemeModeContext);
  if (!ctx) {
    throw new Error('useThemeMode must be used within MUIThemeProvider');
  }
  return ctx;
};

const MUIThemeProvider: React.FC<ThemeProps> = ({ children }) => {
  const getInitialMode = useCallback((): ThemeModeE => {
    try {
      const stored = localStorage.getItem(StorageE.ThemeMode);
      if (stored === ThemeModeE.Light || stored === ThemeModeE.Dark) return stored as ThemeModeE;
    } catch {
      // Fallback to default theme
    }
    return ThemeModeE.Light;
  }, []);

  const [mode, setMode] = useState<ThemeModeE>(getInitialMode);

  useEffect(() => {
    try {
      localStorage.setItem(StorageE.ThemeMode, mode);
    } catch {
      // Theme still works in current session
    }
  }, [mode]);

  useEffect(() => {
    const onStorage = (e: StorageEvent): void => {
      if (
        e.key === StorageE.ThemeMode &&
        (e.newValue === ThemeModeE.Light || e.newValue === ThemeModeE.Dark)
      ) {
        setMode(e.newValue as ThemeModeE);
      }
    };
    window.addEventListener('storage', onStorage);
    return (): void => window.removeEventListener('storage', onStorage);
  }, []);

  const toggleMode = useCallback(() => {
    setMode(prev => (prev === ThemeModeE.Dark ? ThemeModeE.Light : ThemeModeE.Dark));
  }, []);

  const theme = useMemo(() => {
    const base = mode === ThemeModeE.Dark ? ChirpDarkTheme : ChirpTheme;
    const created = createTheme({
      ...base,
      palette: { ...base.palette, mode },
      components: {
        MuiPaper: {
          styleOverrides: {
            root: ({
              theme: t,
              ownerState,
            }: {
              theme: Theme;
              ownerState: { elevation?: number };
            }): Record<string, unknown> => {
              const elevation: number = ownerState?.elevation ?? 0;
              const bgTokens = t.palette.background;
              const elevationKey = `paperElevation${elevation}` as keyof typeof bgTokens;
              const fallbackForZero = bgTokens.paperElevation0;
              const resolved =
                bgTokens[elevationKey] || (elevation === 0 ? fallbackForZero : undefined);
              return {
                backgroundColor: resolved || t.palette.background.paper,
              };
            },
          },
        },
        MuiMenu: {
          styleOverrides: {
            paper: ({ theme: t }: { theme: Theme }): Record<string, unknown> => ({
              backgroundColor: t.palette.background.paperElevation16 || t.palette.background.paper,
            }),
            list: (): Record<string, unknown> => ({
              backgroundColor: 'inherit',
            }),
          },
        },
        MuiMenuItem: {
          styleOverrides: {
            root: ({ theme: t }: { theme: Theme }): Record<string, unknown> => ({
              '&.Mui-selected': {
                backgroundColor: 'inherit',
                color: t.palette.primary.main,
                '&:hover': {
                  backgroundColor: t.palette.primary.main,
                  color: t.palette.primary.contrastText,
                  '& .MuiCheckbox-root': { color: t.palette.primary.contrastText },
                  '& .MuiRadio-root': { color: t.palette.primary.contrastText },
                  '& .MuiListItemIcon-root': { color: t.palette.primary.contrastText },
                  '& .MuiListItemText-primary': { color: 'inherit' },
                },
              },
              '&:hover': {
                backgroundColor: t.palette.primary.main,
                color: t.palette.primary.contrastText,
                '& .MuiCheckbox-root': { color: t.palette.primary.contrastText },
                '& .MuiRadio-root': { color: t.palette.primary.contrastText },
                '& .MuiListItemIcon-root': { color: t.palette.primary.contrastText },
                '& .MuiListItemText-primary': { color: 'inherit' },
              },
              '&.Mui-focusVisible': {
                backgroundColor: t.palette.primary.main,
                color: t.palette.primary.contrastText,
                '& .MuiCheckbox-root': { color: t.palette.primary.contrastText },
                '& .MuiRadio-root': { color: t.palette.primary.contrastText },
                '& .MuiListItemIcon-root': { color: t.palette.primary.contrastText },
                '& .MuiListItemText-primary': { color: 'inherit' },
              },
            }),
          },
        },
        MuiTableCell: {
          styleOverrides: {
            head: ({ theme: t }: { theme: Theme }): Record<string, unknown> => ({
              backgroundColor: t.palette.background.paperElevation0 || t.palette.background.paper,
              color: t.palette.text.primary,
            }),
            stickyHeader: ({ theme: t }: { theme: Theme }): Record<string, unknown> => ({
              backgroundColor: t.palette.background.paperElevation0 || t.palette.background.paper,
            }),
          },
        },
        MuiTabs: {
          styleOverrides: {
            root: {
              borderBottom: 'none',
            },
            indicator: {
              display: 'none',
            },
          },
        },
        MuiTab: {
          styleOverrides: {
            root: ({ theme: t }: { theme: Theme }): Record<string, unknown> => ({
              border: 'none',
              '&.Mui-selected': {
                backgroundColor: t.palette.background.default,
                marginBottom: '-1px',
                zIndex: 1,
              },
            }),
          },
        },
      },
    });
    return responsiveFontSizes(created);
  }, [mode]);

  return (
    <ThemeModeContext.Provider value={{ mode, toggleMode }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalStyles styles={createScrollbarStyles(theme.palette)} />
        <GlobalStyles
          styles={(t: Theme): Record<string, unknown> => ({
            'html, body, #root': {
              backgroundColor:
                t.palette.background.paperElevation1 ||
                t.palette.background.default ||
                t.palette.background.paper,
              color: t.palette.text.primary,
              minHeight: '100%',
            },
            'input[type="number"]': {
              MozAppearance: 'textfield',
            },
            'input[type="number"]::-webkit-inner-spin-button': {
              WebkitAppearance: 'none',
              margin: 0,
              display: 'none',
            },
            'input[type="number"]::-webkit-outer-spin-button': {
              WebkitAppearance: 'none',
              margin: 0,
              display: 'none',
            },
            '.MuiInputBase-input[type="number"]': {
              MozAppearance: 'textfield',
            },
            '.MuiInputBase-input[type="number"]::-webkit-inner-spin-button': {
              WebkitAppearance: 'none',
              margin: 0,
              display: 'none',
            },
            '.MuiInputBase-input[type="number"]::-webkit-outer-spin-button': {
              WebkitAppearance: 'none',
              margin: 0,
              display: 'none',
            },
          })}
        />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};

export default MUIThemeProvider;
